import { Section, SectionEyebrow } from "@/components/site/Section";
import { ResultsDisclaimer } from "@/components/site/ResultsDisclaimer";

export type FAQ = { q: string; a: string };

/**
 * A short, quotable direct answer placed near the top of a page.
 * Marked up with schema.org Question/Answer microdata so answer engines
 * can lift it verbatim, and mirrored in the page's FAQPage JSON-LD.
 */
export function DirectAnswer({
  question,
  answer,
  tone = "darker",
}: {
  question: string;
  answer: string;
  tone?: "dark" | "darker" | "cream";
}) {
  const cream = tone === "cream";
  return (
    <Section tone={tone} className="py-14 md:py-16">
      <div
        itemScope
        itemType="https://schema.org/Question"
        className="max-w-4xl"
      >
        <SectionEyebrow className={cream ? "text-cream-foreground/70" : undefined}>
          Short answer
        </SectionEyebrow>
        <h2
          itemProp="name"
          className={`font-display text-2xl md:text-3xl leading-snug ${
            cream ? "text-cream-foreground" : ""
          }`}
        >
          {question}
        </h2>
        <div
          itemProp="acceptedAnswer"
          itemScope
          itemType="https://schema.org/Answer"
        >
          <p
            itemProp="text"
            className={`mt-5 text-lg md:text-xl leading-relaxed ${
              cream ? "text-cream-foreground/85" : "text-foreground/90"
            }`}
          >
            {answer}
          </p>
        </div>
      </div>
    </Section>
  );
}

/** Expanded FAQ list. Answers stay visible to crawlers inside <details>. */
export function FAQSection({
  faqs,
  eyebrow = "Questions agents ask",
  heading,
  tone = "dark",
  disclaimerQuestions = [],
}: {
  faqs: FAQ[];
  eyebrow?: string;
  heading: string;
  tone?: "dark" | "darker" | "cream";
  disclaimerQuestions?: string[];
}) {
  const cream = tone === "cream";
  return (
    <Section tone={tone}>
      <SectionEyebrow className={cream ? "text-cream-foreground/70" : undefined}>
        {eyebrow}
      </SectionEyebrow>
      <h2
        className={`headline-lg mb-10 max-w-3xl ${
          cream ? "text-cream-foreground" : ""
        }`}
      >
        {heading}
      </h2>
      <div
        className={`divide-y border-y ${
          cream ? "divide-cream-foreground/15 border-cream-foreground/15" : "divide-border border-border"
        }`}
      >
        {faqs.map((f) => (
          <details
            key={f.q}
            className="py-6 group"
            itemScope
            itemType="https://schema.org/Question"
          >
            <summary className="cursor-pointer list-none flex items-start justify-between gap-6">
              <h3
                itemProp="name"
                className={`font-display text-xl md:text-2xl pr-4 ${
                  cream ? "text-cream-foreground" : ""
                }`}
              >
                {f.q}
              </h3>
              <span className="text-primary text-2xl leading-none mt-1 group-open:rotate-45 transition-transform">
                +
              </span>
            </summary>
            <div
              itemProp="acceptedAnswer"
              itemScope
              itemType="https://schema.org/Answer"
            >
              <p
                itemProp="text"
                className={`mt-4 leading-relaxed max-w-3xl ${
                  cream ? "text-cream-foreground/85" : "text-foreground/90"
                }`}
              >
                {f.a}
              </p>
            </div>
            {disclaimerQuestions.includes(f.q) ? <ResultsDisclaimer /> : null}
          </details>
        ))}
      </div>
    </Section>
  );
}

/** FAQPage JSON-LD script entry for a route's head(). */
export function faqSchema(faqs: FAQ[], url: string) {
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      url,
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    }),
  };
}
